import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Box, Button, Drawer, Divider, IconButton, Typography } from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

// Generic timeline item type
export type TimelineItemData = {
  id: string;
  status: string;
  timestamp: string;
  originalTimestamp?: string;
  amount?: string;
  type?: 'system' | 'manual';
  deliveryStatus?: string;
  metadata?: Record<string, any>;
};

// Action button configuration
export type ActionButtonConfig = {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  startIcon?: string;
  endIcon?: string;
  variant?: 'contained' | 'outlined' | 'text';
  color?: string;
  backgroundColor?: string;
  hoverColor?: string;
  fullWidth?: boolean;
  sx?: object;
};

// Header configuration
export type HeaderConfig = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  status?: React.ReactNode;
  actions?: React.ReactNode;
};

// Timeline configuration
export type TimelineConfig = {
  items: TimelineItemData[];
  title?: React.ReactNode;
  itemSpacing?: number;
  showConnectors?: boolean;
  backgroundColor?: string;
  dotColor?: (item: TimelineItemData, index: number) => string;
  dotSx?: (item: TimelineItemData, index: number) => object;
  connectorSx?: object;
  renderItem?: (item: TimelineItemData, index: number) => React.ReactNode;
};

// Overview section configuration
export type OverviewConfig = {
  sections: Array<{
    label: string;
    value: string | React.ReactNode;
    labelSx?: object;
    valueSx?: object;
  }>;
  containerSx?: object;
  sectionSx?: object;
};

type TimelineDrawerProps = {
  open: boolean;
  onClose: () => void;
  width?: number;
  maxWidth?: string;
  padding?: number;

  // Header configuration
  header?: HeaderConfig;
  showHeaderDivider?: boolean;

  // Overview section
  overview?: OverviewConfig;
  showOverviewDivider?: boolean;

  // Timeline configuration
  timeline?: TimelineConfig;

  // Action button
  actionButton?: ActionButtonConfig;
  showActionButton?: boolean;

  children?: React.ReactNode;
  beforeTimeline?: React.ReactNode;
  afterTimeline?: React.ReactNode;

  // Layout
  scrollable?: boolean;
  maxHeight?: string;
  showDivider?: boolean;
};

export function TimelineDrawer({
  open,
  onClose,
  width = 400,
  maxWidth = '90vw',
  padding = 2,
  header,
  showHeaderDivider = true,
  overview,
  showOverviewDivider = true,
  timeline,
  actionButton,
  showActionButton = true,
  children,
  beforeTimeline,
  afterTimeline,
  scrollable = true,
  maxHeight = '60vh',
  showDivider = true,
}: TimelineDrawerProps) {
  const defaultItemRenderer = (item: TimelineItemData) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 0.5,
      }}
    >
      <Box>
        <Typography variant="subtitle2" fontWeight={500}>
          {item.status}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={400}>
          {item.timestamp}
        </Typography>
      </Box>

      {item.amount && (
        <Box
          sx={{
            backgroundColor: '#1C252E',
            color: 'white',
            borderRadius: '6px',
            px: 1.5,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontWeight: 700,
            fontSize: '12px',
          }}
        >
          {item.amount}
        </Box>
      )}
    </Box>
  );

  const renderTimeline = () => {
    if (!timeline || !timeline.items.length) return null;

    return (
      <Box sx={{ backgroundColor: timeline.backgroundColor || 'transparent' }}>
        {timeline.title && <Box sx={{ mb: 2, ml: 2 }}>{timeline.title}</Box>}

        <Timeline
          sx={{
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
              ml: 2,
            },
            p: 0,
          }}
        >
          {timeline.items.map((item, index) => (
            <TimelineItem key={item.id} sx={{ mb: timeline.itemSpacing || 0.5 }}>
              <TimelineSeparator>
                <TimelineDot
                  variant="outlined"
                  color={timeline.dotColor ? (timeline.dotColor(item, index) as any) : 'primary'}
                  sx={timeline.dotSx ? timeline.dotSx(item, index) : {}}
                />

                {timeline.showConnectors !== false && index !== timeline.items.length - 1 && (
                  <TimelineConnector sx={timeline.connectorSx || {}} />
                )}
              </TimelineSeparator>

              <TimelineContent>
                {timeline.renderItem ? timeline.renderItem(item, index) : defaultItemRenderer(item)}
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
        <Divider sx={{ borderStyle: 'dashed' }} />
      </Box>
    );
  };

  const renderOverview = () => {
    if (!overview || !overview.sections.length) return null;

    return (
      <Box sx={{ mb: 1, ...overview.containerSx }}>
        {overview.sections.map((section, index) => (
          <Box key={index} sx={{ mb: 0.5, ...overview.sectionSx }}>
            <Typography
              variant="subtitle2"
              color="grey.600"
              sx={{ fontWeight: 500, fontSize: '18px', ...section.labelSx }}
            >
              {section.label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 500,
                fontFamily: 'Inter Variable',
                ...section.valueSx,
              }}
            >
              {section.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderActionButton = () => {
    if (!showActionButton || !actionButton) return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 2,
          right: 2,
          p: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Button
          variant={actionButton.variant || 'contained'}
          startIcon={actionButton.startIcon ? <Iconify icon={actionButton.startIcon} /> : undefined}
          endIcon={actionButton.endIcon ? <Iconify icon={actionButton.endIcon} /> : undefined}
          onClick={actionButton.onClick}
          disabled={actionButton.disabled || actionButton.loading}
          fullWidth={actionButton.fullWidth}
          sx={{
            backgroundColor: actionButton.backgroundColor || actionButton.color,
            color: actionButton.color ? 'white' : undefined,
            fontWeight: 500,
            py: 1.5,
            '&:hover': {
              backgroundColor: actionButton.hoverColor,
            },
            ...actionButton.sx,
          }}
        >
          {actionButton.loading ? 'Processing...' : actionButton.label}
        </Button>
      </Box>
    );
  };

  const content = (
    <>
      {/* Before Timeline Slot */}
      {beforeTimeline}

      {/* Overview Section */}
      {renderOverview()}

      {/* Overview Divider */}
      {overview && showOverviewDivider && showDivider && (
        <Box sx={{ backgroundColor: timeline?.backgroundColor || 'transparent', p: 2, pt: 0 }}>
          <Divider sx={{ borderStyle: 'dashed' }} />
        </Box>
      )}

      {/* Timeline */}
      {renderTimeline()}

      {/* After Timeline Slot */}
      {afterTimeline}

      {/* Children Slot (Default) */}
      {children}
    </>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { invisible: true } }}
      PaperProps={{
        sx: {
          width,
          maxWidth,
          p: padding,
        },
      }}
    >
      {/* Header */}
      {header && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {header.title}
              {header.subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {header.subtitle}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {header.status}
              {header.actions}
              <IconButton onClick={onClose} size="small">
                <Iconify icon="lucide:x" />
              </IconButton>
            </Box>
          </Box>

          {showHeaderDivider && showDivider && <Divider sx={{ borderStyle: 'dashed', mb: 2 }} />}
        </>
      )}

      {/* Content */}
      {scrollable ? <Scrollbar sx={{ flex: 1, maxHeight }}>{content}</Scrollbar> : content}
      {/* Action Button */}
      {renderActionButton()}
    </Drawer>
  );
}
